import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import PropTypes from 'prop-types';
import { API_TASK_BASE_URL, API_S3_BASE_URL } from '../../../../../apiConstant';
import Modal from '../../../../../uikit/modal';
import Button from '../../../../../uikit/button';
import Search from '../../../../../uikit/search';
import useInputChange from '../../../../../uikit/common_hooks/useInputChange';
import InfiniteScroll from '../../../../../uikit/infiniteScroll';
import { getCurrentLocation } from '../../../../../utils/locationUtils';
import { FILE_TYPES } from '../../../../../utils/constants';

// Helper functions for task status styling
const getStatusColor = (status = '') => {
  switch (status?.toUpperCase()) {
    case 'COMPLETED':
      return 'text-green-600';
    case 'IN_PROGRESS':
      return 'text-blue-600';
    case 'PLANNED':
      return 'text-yellow-600';
    case 'BLOCKED':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
};

const getStatusBgColor = (status = '') => {
  switch (status?.toUpperCase()) {
    case 'COMPLETED':
      return 'bg-green-100';
    case 'IN_PROGRESS':
      return 'bg-blue-100';
    case 'PLANNED':
      return 'bg-yellow-100';
    case 'BLOCKED':
      return 'bg-red-100';
    default:
      return 'bg-gray-100';
  }
};

const TaskCard = ({
  selected,
  disabled = false,
  taskName,
  taskDescription,
  taskStatus,
  tag,
  dueDate,
  onCardClick = () => {},
  taskId = '',
  fullDetails = {},
}) => {
  return (
    <div
      onClick={() => {
        if (!disabled) {
          onCardClick(taskId, fullDetails);
        }
      }}
      className={`flex justify-between cursor-pointer gap-4 px-3 border-b border-[#e9edef] py-3 ${
        selected ? 'bg-[#f0f2f5]' : 'hover:bg-gray-50'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <div className="flex-shrink-0">
        <div className="w-10 h-10 bg-[#FF9900] rounded-full flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
      <div className="flex-shrink-0 w-3/5 max-w-[60vw]">
        <div className="mb-1 text-base font-medium truncate">{taskName}</div>
        <div className="text-sm text-[#919292] font-medium truncate mb-1">
          {taskDescription || 'No description'}
        </div>
        <div className="flex gap-2 items-center">
          <span
            className={`text-xs px-2 py-1 rounded-full ${getStatusBgColor(taskStatus)} ${getStatusColor(taskStatus)}`}
          >
            {taskStatus}
          </span>
          {tag && (
            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
              {tag}
            </span>
          )}
        </div>
      </div>
      <div className="ml-auto flex items-center">
        {selected && (
          <div className="w-6 h-6 bg-[#FF9900] rounded-full flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M20 6L9 17L4 12"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

TaskCard.propTypes = {
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  taskName: PropTypes.string,
  taskDescription: PropTypes.string,
  taskStatus: PropTypes.string,
  tag: PropTypes.string,
  dueDate: PropTypes.string,
  onCardClick: PropTypes.func,
  taskId: PropTypes.string,
  fullDetails: PropTypes.object,
};

const LinkTaskModal = ({
  isOpen,
  onClose,
  fileId,
  projectId,
  initialTaskIds = [],
}) => {
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [selectedTasksFullDetails, setSelectedTasksFullDetails] = useState([]);
  const { inputs, handleInputChange } = useInputChange();
  const queryClient = useQueryClient();

  // Stabilize initialTaskIds to prevent infinite re-renders
  const initialTaskIdsKey = JSON.stringify(initialTaskIds);

  // Handle initial task selection and fetch details
  useEffect(() => {
    if (isOpen && initialTaskIds?.length > 0) {
      const stringTaskIds = initialTaskIds.map((id) => `${id}`);
      setSelectedTaskIds(stringTaskIds);

      // Fetch details for each initial taskId
      const fetchInitialDetails = async () => {
        try {
          const promises = stringTaskIds.map((id) =>
            axios.get(`${API_TASK_BASE_URL}/api/projectTasks/task/${id}`)
          );
          const responses = await Promise.all(promises);
          const details = responses.map((res) => res.data);
          setSelectedTasksFullDetails(details);
        } catch (error) {
          console.error('Error fetching initial task details:', error);
        }
      };

      fetchInitialDetails();
    } else {
      setSelectedTaskIds([]);
      setSelectedTasksFullDetails([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialTaskIdsKey]);

  // Fetch tasks for the project
  const fetchTasks = async (context) => {
    console.log('fetchTasks context:', context);
    const pageParam = context.pageParam || 0;
    const res = await axios.get(
      `${API_TASK_BASE_URL}/api/projectTasks/${projectId}?taskStatus=ALL&page=${pageParam}&size=20${
        inputs?.searchTasks ? `&filterValue=${inputs?.searchTasks}` : ''
      }`
    );
    console.log('fetchTasks returning:', { ...res?.data, number: pageParam });
    return {
      ...res?.data,
      number: pageParam,
    };
  };

  const linkTasksMutation = useMutation({
    mutationFn: async (taskIds) => {
      const location = await getCurrentLocation();
      const promises = taskIds.map((taskId) => {
        return axios.post(`${API_S3_BASE_URL}/api/s3/upload`, {}, {
          params: {
            // ...location,
            taskId,
            type: FILE_TYPES.PROJECT_FILES,
            id: fileId,
          },
        });
      });
      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['project', projectId]);
      onClose();
      window.triggerAlert({
        title: 'Tasks linked successfully',
        type: 'success',
      });
    },
    onError: (error) => {
      console.error('Error linking tasks:', error);
      window.triggerAlert({
        title: 'Failed to link tasks',
        type: 'error',
      });
    },
  });

  const isInitialTask = (taskId) => {
    return initialTaskIds?.map((id) => `${id}`).includes(`${taskId}`);
  };

  const handleTaskToggle = (taskId, taskDetails) => {
    setSelectedTaskIds((prev) => {
      const index = prev.indexOf(taskId);
      if (index > -1) {
        // Already selected — only allow removal if NOT an initial (already assigned) task
        if (isInitialTask(taskId)) return prev;
        return [...prev.slice(0, index), ...prev.slice(index + 1)];
      } else {
        // Prevent duplicate selection
        if (prev.includes(taskId)) return prev;
        return [...prev, taskId];
      }
    });

    setSelectedTasksFullDetails((prev) => {
      const existingTaskIndex = prev.findIndex((t) => `${t.taskId}` === `${taskId}`);
      if (existingTaskIndex > -1) {
        // Already exists — only allow removal if NOT an initial task
        if (isInitialTask(taskId)) return prev;
        return [
          ...prev.slice(0, existingTaskIndex),
          ...prev.slice(existingTaskIndex + 1),
        ];
      } else {
        return [...prev, { ...taskDetails }];
      }
    });
  };

  const handleSave = () => {
    if (selectedTaskIds.length > 0) {
      linkTasksMutation.mutate(selectedTaskIds);
    } else {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Link Tasks to File"
      containerClz="w-full md:w-[850px]"
    >
      <div className="flex flex-col h-[500px]">
        <div className="flex bg-white h-full overflow-hidden">
          {/* Task Search Section */}
          <div className="w-[350px] flex-shrink-0 py-3 border-r overflow-hidden flex flex-col">
            <div className="px-3 mb-4">
              <div className="font-semibold mb-2">Search Tasks</div>
              <Search
                placeholder="Search task name..."
                name="searchTasks"
                onChange={handleInputChange}
              />
            </div>
            <div className="flex-1 overflow-hidden">
              <InfiniteScroll
                fetchFn={fetchTasks}
                queryKey={[
                  'projectTasks-linking',
                  projectId,
                  { inputValue: inputs?.searchTasks },
                ]}
                renderItem={(task) => (
                  <TaskCard
                    key={task.taskId}
                    selected={selectedTaskIds.includes(`${task.taskId}`)}
                    taskName={task.taskName}
                    taskDescription={task.taskDescription}
                    taskStatus={task.taskStatus}
                    tag={task.tag?.tagName}
                    taskId={`${task.taskId}`}
                    fullDetails={task}
                    onCardClick={handleTaskToggle}
                  />
                )}
                loadingIndicator={<div className="p-3 text-center text-sm text-gray-500">Loading tasks...</div>}
                emptyIndicator={null}
              />
            </div>
          </div>

          {/* Selected Tasks Section */}
          <div className="flex-1 min-w-0 px-3 py-3 flex flex-col overflow-hidden">
            <div className="font-semibold mb-4 text-[#A86808]">
              Selected Tasks ({selectedTaskIds.length})
            </div>
            <div className="flex-1 overflow-y-auto space-y-2">
              {selectedTasksFullDetails.length > 0 ? (
                selectedTasksFullDetails.map((task) => (
                  <div
                    key={task.taskId}
                    className="bg-gray-50 p-3 rounded-lg border flex justify-between items-center"
                  >
                    <div className="overflow-hidden mr-2">
                      <div className="font-medium text-sm truncate">{task.taskName}</div>
                      <div className="text-xs text-gray-500 truncate">{task.taskStatus}</div>
                    </div>
                    {/* <Button
                      variant="text"
                      size="small"
                      onClick={() => handleTaskToggle(`${task.taskId}`, task)}
                      startIcon={<img src={'/images/delete.svg'} alt="remove" className="w-4 h-4" />}
                    /> */}
                  </div>
                ))
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-gray-400 border-2 border-dashed rounded-lg">
                  No tasks selected.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-2 border-t  flex-shrink-0">
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={linkTasksMutation.isLoading}
          >
            {linkTasksMutation.isLoading ? 'Linking...' : 'Save Links'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

LinkTaskModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  fileId: PropTypes.string,
  projectId: PropTypes.string,
  initialTaskIds: PropTypes.array,
};

export default LinkTaskModal;
